import * as React from 'react';
import { CheckCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  stepIndicatorRecipe,
  stepIndicatorStepRecipe,
  stepIndicatorTileRecipe,
  type StepIndicatorRecipeProps,
} from '../recipes/step-indicator.recipe';
import { Icon } from './Icon';
import { Inline, Stack } from './Layout';
import { ProgressBar } from './Progress';
import { Text } from './Text';

export interface StepIndicatorStep {
  id: number;
  title: React.ReactNode;
  icon: LucideIcon;
  /** The step's form is filled in; draws the tick. */
  complete?: boolean;
  /** The step can be jumped to. The page decides — usually "everything before it is complete". */
  reachable?: boolean;
}

export interface StepIndicatorProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'>,
    StepIndicatorRecipeProps {
  steps: StepIndicatorStep[];
  /** The id of the step being shown. */
  current: number;
  onSelect?: (id: number) => void;
  /**
   * Below the list — the vertical column's "Schedule demo" button. Ignored
   * on the horizontal strip, which has no room for it.
   */
  children?: React.ReactNode;
}

/**
 * Where you are in a multi-step form.
 *
 *   <StepIndicator
 *     orientation="vertical"
 *     current={2}
 *     steps={[{ id: 1, title: 'Contact info', icon: Users, complete: true, reachable: true }, …]}
 *     onSelect={goToStep}
 *   />
 *
 * `horizontal` is the strip under a phone header: a "Step 2 of 3" line, a
 * progress bar and a row of tiles. `vertical` is the desktop sidebar: a
 * "2 / 3" count and a column of rows. Render both and let `Show` pick — the
 * onboarding page does — rather than measuring the viewport.
 */
export const StepIndicator = React.forwardRef<
  HTMLDivElement,
  StepIndicatorProps
>(function StepIndicator(
  {
    steps,
    current,
    onSelect,
    orientation = 'horizontal',
    className,
    children,
    ...props
  },
  ref
) {
  const total = steps.length;
  const index = steps.findIndex((step) => step.id === current);
  const position = index === -1 ? 0 : index + 1;
  const currentStep = index === -1 ? undefined : steps[index];

  const stateOf = (step: StepIndicatorStep) =>
    step.id === current ? 'current' : step.complete ? 'complete' : 'pending';

  const renderStep = (step: StepIndicatorStep) => {
    const state = stateOf(step);
    const glyph = (
      <Icon icon={step.complete ? CheckCircle : step.icon} size="md" />
    );
    return (
      <button
        key={step.id}
        type="button"
        disabled={!step.reachable}
        aria-current={step.id === current ? 'step' : undefined}
        onClick={() => onSelect?.(step.id)}
        className={stepIndicatorStepRecipe({ orientation, state })}
      >
        {orientation === 'horizontal' ? (
          <span className={stepIndicatorTileRecipe({ state })}>{glyph}</span>
        ) : (
          glyph
        )}
        {orientation === 'horizontal' ? (
          <Text variant="caption" tone="muted" as="span">
            {step.title}
          </Text>
        ) : (
          <Text variant="body-strong" tone="inherit" as="span">
            {step.title}
          </Text>
        )}
      </button>
    );
  };

  if (orientation === 'vertical') {
    return (
      <div
        ref={ref}
        className={cn(stepIndicatorRecipe({ orientation }), className)}
        {...props}
      >
        <Stack gap={6}>
          <Text variant="h2" tone="muted" numeric as="p">
            {position} / {total}
          </Text>
          <Stack gap={1} role="group" aria-label="Steps">
            {steps.map(renderStep)}
          </Stack>
        </Stack>
        {children}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cn(stepIndicatorRecipe({ orientation }), className)}
      {...props}
    >
      <Inline gap={2} justify="between" className="mb-2">
        <Text variant="body-strong" tone="muted" as="span">
          Step {position} of {total}
        </Text>
        <Text variant="body-sm" tone="info" as="span">
          {currentStep?.title}
        </Text>
      </Inline>
      <ProgressBar
        size="lg"
        value={total ? (position / total) * 100 : 0}
        label="Progress through the steps"
      />
      <Inline
        gap={2}
        align="stretch"
        justify="between"
        role="group"
        aria-label="Steps"
        className="mt-4"
      >
        {steps.map(renderStep)}
      </Inline>
    </div>
  );
});
